import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimesCircle,
  faHome,
  faRedo,
  faQuestionCircle,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import logoImage from '../assets/images/logo.webp';

export default function Cancel() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center px-4">
      <div className="max-w-2xl mx-auto text-center">
        {/* Logo */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <img src={logoImage} alt="Wincrest Logistics" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800">Wincrest Logistics</h1>
        </motion.div>

        {/* Cancel Icon */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="relative">
            <FontAwesomeIcon 
              icon={faTimesCircle} 
              className="text-8xl text-orange-500 mb-4 drop-shadow-lg" 
            />
            <div className="absolute -top-2 -right-2 bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center">
              ✕
            </div>
          </div>
          <h2 className="text-4xl font-bold text-gray-800 mb-2">Payment Cancelled</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-red-600 mx-auto rounded-full"></div>
        </motion.div>

        {/* Cancel Message */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3 className="text-2xl font-bold text-gray-800 mb-4">
            No Worries, It Happens!
          </h3>
          <p className="text-gray-600 text-lg leading-relaxed mb-6">
            Your payment was not completed, but that's perfectly fine. No charges were made 
            to your account and you can try again whenever you're ready.
          </p>
          
          {/* Helpful Information */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center justify-center">
              <FontAwesomeIcon icon={faQuestionCircle} className="mr-2 text-orange-500" />
              What you can do now:
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="font-medium text-gray-800">Try Again</p>
                  <p className="text-sm text-gray-600">Return to our services and complete your request</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="font-medium text-gray-800">Browse Services</p>
                  <p className="text-sm text-gray-600">Explore our logistics solutions</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="font-medium text-gray-800">Get Support</p>
                  <p className="text-sm text-gray-600">Contact us if you need assistance</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="font-medium text-gray-800">No Charges</p>
                  <p className="text-sm text-gray-600">Your payment method was not charged</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <button
            type="button"
            onClick={() => navigate('/')}
            className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          >
            <FontAwesomeIcon icon={faHome} className="mr-3 group-hover:animate-bounce" />
            Return to Homepage
            <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
              →
            </div>
          </button>

          <button
            type="button"
            onClick={() => navigate('/#services')}
            className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-orange-600 to-red-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          >
            <FontAwesomeIcon icon={faRedo} className="mr-3 group-hover:animate-spin" />
            Try Again
            <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
              ↻
            </div>
          </button>

          <a
            href="mailto:email-pending"
            className="group inline-flex items-center justify-center px-8 py-4 bg-white text-gray-700 font-semibold rounded-xl shadow-lg border border-gray-200 hover:shadow-xl hover:bg-gray-50 transform hover:scale-105 transition-all duration-300"
          >
            <FontAwesomeIcon icon={faEnvelope} className="mr-3 group-hover:animate-pulse" />
            Get Help
            <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
              ✉
            </div>
          </a>
        </motion.div>

        {/* Footer */}
        <motion.div
          className="mt-12 pt-8 border-t border-gray-200"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <p className="text-gray-500 text-sm">
            Having trouble with payments? Contact us at{' '}
            <a 
              href="mailto:email-pending" 
              className="text-orange-600 hover:text-orange-800 font-medium transition-colors"
            >
              email-pending
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
